// src/App.js
import React, { useEffect } from 'react';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Home from './components/Home';
import ServiceCenterSelect from './components/ServiceCenterSelect.jsx';
import { AuthContext } from './AuthContext';
import Cart from './components/Cart';
import StorekeeperDashboard from './components/StorekeeperDashboard';
import OTPVerification from './components/OTPVerification.jsx';
import ReportDetails from './components/ReportDetails';
import ReportConstructorPage from './components/ReportConstructorPage';
import FileUploadComponent from './components/FileUploadComponent';
import LoginPage from './components/LoginPage';
import {
    isLikelyMisconfiguredUnifiedAuth,
    redirectToUnifiedAuth,
    sanitizeUnifiedAuthReturnPath,
} from './utils/unifiedAuth';

const UnifiedLoginRedirect = () => {
    const location = useLocation();
    const misconfiguredUnifiedAuth = isLikelyMisconfiguredUnifiedAuth();
    const from = location?.state?.from;
    const nextPath = from?.pathname
        ? sanitizeUnifiedAuthReturnPath(from)
        : '/';

    useEffect(() => {
        if (misconfiguredUnifiedAuth) {
            return;
        }
        redirectToUnifiedAuth(nextPath);
    }, [misconfiguredUnifiedAuth, nextPath]);

    if (misconfiguredUnifiedAuth) {
        return <LoginPage />;
    }

    return null;
};

const PrivateRoute = ({ children }) => {
    const location = useLocation();
    const { authTokens } = React.useContext(AuthContext);

    if (!authTokens?.access) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return children;
};

function App() {
    const { authTokens, otpRequired, loading, authBootstrapComplete } = React.useContext(AuthContext);

    if (loading || (authTokens?.access && !authBootstrapComplete)) {
        return (
            <div className="spinner-container">
                <div className="sk-folding-cube">
                    <div className="sk-cube1 sk-cube"></div>
                    <div className="sk-cube2 sk-cube"></div>
                    <div className="sk-cube4 sk-cube"></div>
                    <div className="sk-cube3 sk-cube"></div>
                </div>
            </div>
        );
    }

    return (
        <Routes>
            {otpRequired && authTokens?.access ? (
                <Route path="*" element={<OTPVerification />} />
            ) : (
                <>
                    <Route path="/login" element={<UnifiedLoginRedirect />} />
                    <Route path="/select-service-center" element={<PrivateRoute><ServiceCenterSelect /></PrivateRoute>} />
                    <Route path="/upload-file" element={<PrivateRoute><FileUploadComponent /></PrivateRoute>} />
                    <Route path="/cart" element={<PrivateRoute><Cart /></PrivateRoute>} />
                    <Route path="/storekeeper" element={<PrivateRoute><StorekeeperDashboard /></PrivateRoute>} />
                    <Route path="/report-details" element={<PrivateRoute><ReportDetails /></PrivateRoute>} />
                    <Route path="/report-constructor" element={<PrivateRoute><ReportConstructorPage /></PrivateRoute>} />
                    <Route path="/report-constructor/:templateId" element={<PrivateRoute><ReportConstructorPage /></PrivateRoute>} />
                    <Route path="/" element={<PrivateRoute><Home /></PrivateRoute>} />
                    <Route path="*" element={<Navigate to="/" />} />
                </>
            )}
        </Routes>
    );
}

export default App;
